import React, { useState } from 'react';
import { TransaksiPembayaran, Siswa } from '../../types/index.js';
import { Search, Printer, FileText, CheckCircle2 } from 'lucide-react';
import { useDataTable } from '../../hooks/useDataTable.js';
import { api } from '../../services/api.js';
import { Pagination } from '../ui/Pagination.js';
import { formatPaymentMethod, getPokokNominal, filterPokokItems } from '../../utils/paymentMethod.js';

interface TransaksiSiswaViewProps {
  transaksiList: TransaksiPembayaran[];
  siswaList: Siswa[];
  onShowReceipt: (trx: TransaksiPembayaran) => void;
}

export const TransaksiSiswaView: React.FC<TransaksiSiswaViewProps> = ({
  transaksiList,
  siswaList,
  onShowReceipt,
}) => {
  const [selectedSiswaId, setSelectedSiswaId] = useState('s-13'); // Asfa Neysa
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [filteredTrx, setFilteredTrx] = useState<TransaksiPembayaran[]>([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.getTransaksiPaginated({
          siswaId: selectedSiswaId === 'ALL' ? undefined : selectedSiswaId,
          page: currentPage,
          limit: rowsPerPage,
        });
        if (res.success) {
          setFilteredTrx(res.data);
          setTotalPages(res.totalPages);
          setTotalRows(res.totalCount);
        }
      } catch (err) {
        console.error(err);
      }
    };
    const timer = setTimeout(fetchData, 300);
    return () => clearTimeout(timer);
  }, [selectedSiswaId, search, currentPage, rowsPerPage]);

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Header Matching Document Page 14 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Histori Transaksi Pembayaran Siswa</h2>
          <p className="text-xs text-slate-500">
            Catatan historis seluruh setoran dan pembayaran biaya sekolah per siswa
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari No Transaksi/Kuitansi..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-52"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600 hidden md:inline">Pilih Siswa:</span>
            <select
              value={selectedSiswaId}
              onChange={(e) => setSelectedSiswaId(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 focus:ring-1 focus:ring-sky-500 font-medium min-w-[280px]"
            >
              <option value="ALL">-- Semua Siswa --</option>
              {siswaList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nis} - {s.nama} ({s.kelasNama})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Main List */}
      <div className="space-y-3">
        {filteredTrx.map((trx) => (
          <div
            key={trx.id}
            className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs hover:border-sky-300 transition"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center font-bold">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-bold text-slate-900 text-xs">{trx.noTransaksi}</span>
                    <span className="text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono">
                      {trx.noKuitansi}
                    </span>
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded">
                      {formatPaymentMethod(trx.channel, trx.metodeBayar)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {trx.namaSiswa} ({trx.kelas}) • {trx.tanggal} • Kasir: {trx.kasir}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="text-[11px] text-slate-500 block">Total Dibayar</span>
                  <span className="font-mono text-base font-extrabold text-sky-900">
                    Rp {getPokokNominal(trx).toLocaleString('id-ID')}
                  </span>
                </div>

                <button
                  onClick={() => onShowReceipt(trx)}
                  className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 text-white rounded-md text-xs font-semibold flex items-center gap-1.5 shadow-xs transition"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span>Kuitansi</span>
                </button>
              </div>
            </div>

            {/* Sub-items table */}
            <div className="pt-3">
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1.5 block">
                Item Tagihan Yang Dilunasi:
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                {filterPokokItems(trx.items).map((it, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center bg-slate-50 px-3 py-1.5 rounded border border-slate-100"
                  >
                    <div>
                      <span className="font-semibold text-slate-800">{it.namaTagihan}</span>
                      <span className="text-[10px] text-slate-400 block">{it.posName}</span>
                    </div>
                    <span className="font-mono font-bold text-slate-900">
                      Rp {it.nominal.toLocaleString('id-ID')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {filteredTrx.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
            Tidak ada transaksi pembayaran untuk siswa terpilih
          </div>
        )}
        
        {filteredTrx.length > 0 && (
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            totalRows={totalRows}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={setRowsPerPage}
          />
        )}
      </div>
    </div>
  );
};
